import { ApiError, BaseService } from "@/services/BaseService";
import { SubscriptionPlan } from "@/types/subscription-plan";
import {
  AttachHotelSubscriptionPayload,
  AttachHotelSubscriptionResponse,
  HotelSubscription,
  PaymentUrlResponse,
  ToggleHotelSubscriptionFeaturePayload,
  ToggleHotelSubscriptionFeatureResponse,
} from "@/types/hotel-subscription";
import {
  TenantMergedFeaturesResponse,
  TenantHotelSubscriptionTransactionsFilters,
  TenantHotelSubscriptionTransactionsResponse,
  TenantSubscription,
  TenantSubscriptionTransactionListResponse,
} from "@/types/tenant-subscription";
import { normalizeSubscriptionTransactionsResponse } from "./subscription-transactions-adapter";

class TenantSubscriptionService extends BaseService {
  getPlans(): Promise<SubscriptionPlan[]> {
    return this.get<SubscriptionPlan[]>("/subscriptions/plans");
  }

  async getHotelSubscription(
    hotelId: string,
  ): Promise<HotelSubscription | null> {
    try {
      const subscription = await this.get<HotelSubscription | null>(
        `/hotels/${hotelId}/subscription`,
        undefined,
        { disableToast: true },
      );

      return subscription ?? null;
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null;
      }

      throw error;
    }
  }

  attachHotelPlan(
    hotelId: string,
    planId: string,
    roomsNumber?: number,
  ): Promise<AttachHotelSubscriptionResponse> {
    const payload: AttachHotelSubscriptionPayload = {
      planId,
      ...(roomsNumber !== undefined ? { roomsNumber } : {}),
    };

    return this.put<AttachHotelSubscriptionResponse>(
      `/hotels/${hotelId}/subscription`,
      payload,
    );
  }

  detachHotelPlan(hotelId: string): Promise<void> {
    return this.delete<void>(`/hotels/${hotelId}/subscription`);
  }

  toggleHotelFeature(
    hotelId: string,
    featureKey: string,
    enabled: boolean,
  ): Promise<ToggleHotelSubscriptionFeatureResponse> {
    const payload: ToggleHotelSubscriptionFeaturePayload = { enabled };

    return this.patch<ToggleHotelSubscriptionFeatureResponse>(
      `/hotels/${hotelId}/subscription/features/${encodeURIComponent(featureKey)}`,
      payload,
    );
  }

  getPaymentUrl(
    subscriptionId: string,
    returnUrl: string,
  ): Promise<PaymentUrlResponse> {
    return this.get<PaymentUrlResponse>(
      `/subscriptions/my/${subscriptionId}/payment-url`,
      { returnUrl },
    );
  }

  getPortalUrl(returnUrl: string): Promise<PaymentUrlResponse> {
    return this.get<PaymentUrlResponse>("/subscriptions/my/portal-url", {
      returnUrl,
    });
  }

  getMySubscriptions(): Promise<TenantSubscription[]> {
    return this.get<TenantSubscription[]>("/subscriptions/my");
  }

  getMyFeatures(): Promise<TenantMergedFeaturesResponse> {
    return this.get<TenantMergedFeaturesResponse>("/subscriptions/my/features");
  }

  getMySubscriptionTransactions(
    subscriptionId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<TenantSubscriptionTransactionListResponse> {
    return this.get<TenantSubscriptionTransactionListResponse>(
      `/subscriptions/my/${subscriptionId}/transactions`,
      params,
    );
  }

  async getMyHotelsTransactions(
    params?: TenantHotelSubscriptionTransactionsFilters,
  ): Promise<TenantHotelSubscriptionTransactionsResponse> {
    const response =
      await this.get<TenantHotelSubscriptionTransactionsResponse>(
        "/subscriptions/my/hotels/transactions",
        params,
      );

    return normalizeSubscriptionTransactionsResponse(response, params);
  }
}

export const tenantSubscriptionService = new TenantSubscriptionService();
